import React, { useState, useRef, forwardRef } from 'react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import ImageUploadContent from '../image-upload/ImageUploadContent';
import AvatarGenerationContent from './AvatarGenerationContent';
import OnboardingAvatarContent from './OnboardingAvatarContent';
import CoverImageGenerationContent from '../CoverImageUpload/CoverImageGenerationContent';
import WorkbookImageGenerationContent from './WorkbookImageGenerationContent';
import { ImportImageContent } from './ImportImageContent';
import { useImageUpload } from '../image-upload/useImageUpload';
import { TabbedImageUploadProps } from './types';
import { WandSparkles, Upload, Download } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { AvatarStyleType } from './types';
import { UploadDropzoneRef } from '../UploadDropzone';
import { useLocation } from 'react-router-dom';
import ActionButton from '@/components/ui/ActionButton';

// Smart style detection based on task content
const detectImageStyle = (description: string): string => {
  if (!description) return 'style-photo';
  
  const text = description.toLowerCase();
  
  // Art/Illustration keywords
  const artKeywords = ['art', 'illustration', 'creative', 'artistic', 'design', 'graphic', 'logo', 'icon', 'symbol', 'drawing', 'sketch', 'artwork', 'visual', 'brand', 'style', 'aesthetic', 'conceptual'];
  
  // Anime/Cartoon keywords  
  const animeKeywords = ['character', 'cartoon', 'anime', 'mascot', 'avatar', 'persona', 'stylized', 'animated', 'comic', 'fictional'];
  
  // 3D/Technical keywords
  const threeDKeywords = ['3d', 'model', 'render', 'technical', 'engineering', 'architecture', 'blueprint', 'diagram', 'structure', 'mechanical', 'product', 'prototype', 'dimensional'];
  
  // Count matches for each style
  const artScore = artKeywords.filter(keyword => text.includes(keyword)).length;
  const animeScore = animeKeywords.filter(keyword => text.includes(keyword)).length;
  const threeDScore = threeDKeywords.filter(keyword => text.includes(keyword)).length;
  
  // Return style with highest score
  if (threeDScore > artScore && threeDScore > animeScore) return 'style-3d';
  if (animeScore > artScore && animeScore > threeDScore) return 'style-anime';
  if (artScore > 0) return 'style-art';
  
  // Default to photo for professional/realistic content
  return 'style-photo';
};

interface ExtendedTabbedImageUploadProps extends TabbedImageUploadProps {
  fileInputRef?: React.RefObject<HTMLInputElement>;
  triggerFileInput?: () => void;
  setImageDescription?: (desc: string) => void;
  setImageStyle?: (style: string) => void;
  setImageWidth?: (width: string) => void;
  setImageHeight?: (height: string) => void;
  autoUpload?: boolean;
  mode?: 'avatar' | 'cover' | 'workbook-image';
  workbookId?: string;
  pageId?: string;
  initialDescription?: string;
  initialStyle?: string;
  isOnboarding?: boolean;
  // Footer button props
  showFooterButtons?: boolean;
  footerCancelText?: string;
  footerActionText?: string;
  footerActionIcon?: React.ReactNode;
  footerActionVariant?: string;
  footerActionClassName?: string;
  onFooterCancel?: () => void;
  onFooterAction?: () => void;
  isFooterActionDisabled?: boolean;
  isFooterActionLoading?: boolean;
  onDirectImageImport?: (imageUrl: string, altText?: string) => void;
}

const TabbedImageUpload = forwardRef<UploadDropzoneRef, ExtendedTabbedImageUploadProps>(({
  onUpload,
  onClose,
  allowedTypes = ['image/jpeg', 'image/png', 'image/webp'],
  maxSizeMB = 10, // 10MB - WebP conversion handles optimization
  aspectRatio = 1,
  previewSize = 'md',
  accentColor = 'blue',
  onTabChange,
  // REMOVED: onImportedImageUpload - unified to use onDirectImageImport
  showImportTab = false,
  fileInputRef,
  triggerFileInput,
  setImageDescription,
  setImageStyle,
  setImageWidth,
  setImageHeight,
  autoUpload = false,
  mode = 'avatar',
  workbookId,
  pageId,
  initialDescription = '',
  initialStyle = '',
  isOnboarding = false,
  // Footer button props
  showFooterButtons = false,
  footerCancelText = 'Cancel',
  footerActionText = 'Action',
  footerActionIcon,
  footerActionVariant = 'blue',
  footerActionClassName = '',
  onFooterCancel,
  onFooterAction,
  isFooterActionDisabled = false,
  isFooterActionLoading = false,
  onDirectImageImport // UNIFIED: All imports use direct URL operations
}, ref) => {
  const location = useLocation();
  
  // Auto-detect onboarding context if not explicitly provided
  const autoDetectedOnboarding = location.pathname === '/welcome' || location.pathname === '/onboard';
  const effectiveIsOnboarding = isOnboarding ?? autoDetectedOnboarding;
  
  const [activeTab, setActiveTab] = useState<'import' | 'generate' | 'upload'>('generate');
  const [imageDescription, setLocalImageDescription] = useState(initialDescription);
  
  // Smart style detection: use provided style or detect from description
  const smartDetectedStyle = initialDescription ? detectImageStyle(initialDescription) : 'style-photo';
  const effectiveInitialStyle = initialStyle || smartDetectedStyle;
  
  // Debug logging for smart detection
  React.useEffect(() => {
    if (initialDescription) {
      console.log('[TabbedImageUpload] Smart detection results:', {
        initialDescription,
        smartDetectedStyle,
        effectiveInitialStyle,
        initialStyle
      });
    }
  }, [initialDescription, smartDetectedStyle, effectiveInitialStyle, initialStyle]);
  
  const [imageStyle, setLocalImageStyle] = useState<string>(effectiveInitialStyle);
  
  const [imageWidth, setLocalImageWidth] = useState<string>('1024');
  const [imageHeight, setLocalImageHeight] = useState<string>('1024');
  
  const {
    state: uploadState,
    handleFileSelection,
    handleClose,
    removeSelectedImage,
    handleUpload,
    triggerFileInput: localTriggerFileInput,
    fileInputRef: localFileInputRef
  } = useImageUpload(
    onUpload, 
    onClose, 
    fileInputRef, 
    { autoUpload } 
  );
  
  // Update the tab when upload starts/finishes to ensure UI consistency
  React.useEffect(() => {
    if (uploadState.uploadStatus === 'uploading' && activeTab !== 'upload') {
      setActiveTab('upload');
    }
  }, [uploadState.uploadStatus, activeTab]);

  // Update parent component when tab changes
  const handleTabChange = (value: string) => {
    const newTab = value as 'import' | 'generate' | 'upload';
    setActiveTab(newTab);
    if (onTabChange) {
      onTabChange(newTab);
    }
  };
  
  // Handle image description and style updates
  const updateImageDescription = (description: string) => {
    setLocalImageDescription(description);
    if (setImageDescription) setImageDescription(description);
  };
  
  const updateImageStyle = (style: string) => {
    setLocalImageStyle(style);
    if (setImageStyle) setImageStyle(style);
  };

  const updateImageWidth = (width: string) => {
    setLocalImageWidth(width);
    if (setImageWidth) setImageWidth(width);
  };

  const updateImageHeight = (height: string) => {
    setLocalImageHeight(height);
    if (setImageHeight) setImageHeight(height);
  };
  
  // Handle file selection with proper error handling
  const handleFileSelectionWithErrorHandling = async (file: File) => {
    try {
      await handleFileSelection(file);
    } catch (error) {
      console.error('Error handling file selection:', error);
    }
  };

  // Use the provided triggerFileInput function or the local one
  const effectiveTriggerFileInput = triggerFileInput || localTriggerFileInput;
  const effectiveFileInputRef = fileInputRef || localFileInputRef;

  // For onboarding mode, show simplified content without tabs
  if (effectiveIsOnboarding && mode === 'avatar') {
    return (
      <div>
        <OnboardingAvatarContent
          setDescription={updateImageDescription}
          setStyle={updateImageStyle}
        />
        
        {/* Footer Buttons for Onboarding */}
        {showFooterButtons && (
          <div className="w-full flex justify-between items-center pt-4">
            <ActionButton 
              variant="cancel" 
              onClick={onFooterCancel}
              disabled={isFooterActionDisabled}
            >
              {footerCancelText}
            </ActionButton>
            
            <ActionButton 
              variant={footerActionVariant as any} 
              icon={footerActionIcon}
              onClick={onFooterAction}
              disabled={isFooterActionDisabled}
              loading={isFooterActionLoading}
              className={footerActionClassName}
            >
              {footerActionText}
            </ActionButton>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <Tabs 
        value={activeTab} 
        onValueChange={(value) => handleTabChange(value as 'import' | 'generate' | 'upload')}
        className="w-full"
      >
        {/* Tab navigation - smart conditional display */}
        {(() => {
          // Special case for onboarding: Only show the generate tab
          if (effectiveIsOnboarding) {
            return (
              <div className="flex items-center justify-center mb-2">
                <span className="text-sm font-medium text-muted-foreground">
                  ✨ Generate your avatar with AI
                </span>
              </div>
            );
          }

          // For modes that support generate and upload (conditionally import)
          if (mode === 'avatar' || mode === 'cover' || mode === 'workbook-image') {
            return (
              <div className={cn(
                "flex rounded-lg bg-muted p-1 text-muted-foreground grid w-full mb-2",
                showImportTab ? "grid-cols-3" : "grid-cols-2"
              )}>
                {showImportTab && (
                  <motion.button
                    className={cn(
                      "inline-flex items-center justify-center whitespace-nowrap rounded-md px-2 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative",
                      activeTab === 'import' 
                        ? "bg-background text-foreground shadow-sm" 
                        : "text-muted-foreground hover:text-foreground"
                    )}
                    onClick={() => handleTabChange('import')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Import
                  </motion.button>
                )}
                <motion.button
                  className={cn(
                    "inline-flex items-center justify-center whitespace-nowrap rounded-md px-2 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative",
                    activeTab === 'generate' 
                      ? "bg-background text-foreground shadow-sm" 
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  onClick={() => handleTabChange('generate')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <WandSparkles className="w-4 h-4 mr-1" />
                  Generate
                </motion.button>
                <motion.button
                  className={cn(
                    "inline-flex items-center justify-center whitespace-nowrap rounded-md px-2 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 relative",
                    activeTab === 'upload' 
                      ? "bg-background text-foreground shadow-sm" 
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  onClick={() => handleTabChange('upload')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Upload className="w-4 h-4 mr-1" />
                  Upload
                </motion.button>
              </div>
            );
          }

          return null;
        })()}

        {showImportTab && (
          <TabsContent value="import" className="mt-2">
            <ImportImageContent
              workbookId={workbookId}
              onImageImport={async (imageUrl, altText) => {
                console.log('[TabbedImageUpload] Image import requested:', { imageUrl, altText });
                
                // NEW: Direct URL insertion for editor contexts (avoids duplication)
                if (onDirectImageImport) {
                  console.log('[TabbedImageUpload] Using direct URL insertion for editor context');
                  try {
                    onDirectImageImport(imageUrl, altText);
                    onClose(); // Close the modal after direct insertion
                  } catch (error) {
                    console.error('Error with direct image import:', error);
                  }
                } else {
                  console.error('No import handler provided (onDirectImageImport)');
                }
              }}
              accentColor={accentColor}
            />
          </TabsContent>
        )}

        <TabsContent value="generate" className="mt-2">
          {mode === 'avatar' ? (
            <AvatarGenerationContent 
              aspectRatio={aspectRatio}
              setDescription={updateImageDescription}
              setStyle={updateImageStyle}
            />
          ) : mode === 'cover' ? (
            <CoverImageGenerationContent
              setDescription={updateImageDescription}
              setStyle={updateImageStyle}
              initialDescription={initialDescription}
              initialStyle={imageStyle}
            />
          ) : (
            <WorkbookImageGenerationContent
              setDescription={updateImageDescription}
              setStyle={updateImageStyle}
              setWidth={updateImageWidth}
              setHeight={updateImageHeight}
              initialDescription={initialDescription} // NEW: Pass initial description
              initialStyle={imageStyle} // NEW: Pass initial style (which includes smart detection)
            />
          )}
        </TabsContent>

        <TabsContent value="upload" className="mt-2">
          <ImageUploadContent
            ref={ref}
            state={uploadState}
            handleFileSelection={handleFileSelectionWithErrorHandling}
            removeSelectedImage={removeSelectedImage}
            allowedTypes={allowedTypes}
            maxSizeMB={maxSizeMB}
            aspectRatio={aspectRatio}
            previewSize={previewSize}
            triggerFileInput={effectiveTriggerFileInput}
            fileInputRef={effectiveFileInputRef}
            autoUpload={autoUpload}
          />
        </TabsContent>
      </Tabs>
      
      {/* Inline Footer Buttons */}
      {showFooterButtons && (
        <div className="w-full flex justify-between items-center pt-4">
          <ActionButton 
            variant="cancel" 
            onClick={onFooterCancel}
            disabled={isFooterActionDisabled}
          >
            {footerCancelText}
          </ActionButton>
          
          <ActionButton 
            variant={footerActionVariant as any} 
            icon={footerActionIcon}
            onClick={onFooterAction}
            disabled={isFooterActionDisabled}
            loading={isFooterActionLoading}
            className={footerActionClassName}
          >
            {footerActionText}
          </ActionButton>
        </div>
      )}
    </div>
  );
});

TabbedImageUpload.displayName = 'TabbedImageUpload';

export default TabbedImageUpload;
